import React from 'react';
import { Card, Typography, Row, Col } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import CardImage from '../CardImage';
import './DeckCardPicker.css';

const { Text } = Typography;

/**
 * Pick cards for opening hand from session deck.
 * deckCards: [{ card_id, name, img_full_url, quantity }]
 * value: string[] of card_id (selected hand, duplicates allowed)
 * onChange: (cardIds: string[]) => void
 * maxSelection: number (e.g. 5)
 */
export default function DeckCardPicker({ deckCards = [], value = [], onChange, maxSelection = 5 }) {
  const selected = value || [];
  const countByCard = selected.reduce((acc, id) => {
    acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});

  const addCard = (cardId) => {
    if (selected.length >= maxSelection) return;
    const inDeck = deckCards.find((c) => c.card_id === cardId)?.quantity ?? 0;
    const alreadySelected = countByCard[cardId] || 0;
    if (alreadySelected >= inDeck) return;
    if (typeof onChange === 'function') onChange([...selected, cardId]);
  };

  const removeCard = (index) => {
    const next = [...selected];
    next.splice(index, 1);
    if (typeof onChange === 'function') onChange(next);
  };

  if (deckCards.length === 0) {
    return (
      <div className="deck-card-picker-empty">
        <Text type="secondary">Save a deck first to pick opening hand.</Text>
      </div>
    );
  }

  return (
    <div className="deck-card-picker">
      <div className="deck-card-picker-hand">
        <Text type="secondary" strong>
          Selected ({selected.length}/{maxSelection})
        </Text>
        <div className="deck-card-picker-hand-cards">
          {selected.map((cardId, idx) => {
            const card = deckCards.find((c) => c.card_id === cardId);
            return (
              <div
                key={`${cardId}-${idx}`}
                className="deck-card-picker-hand-slot"
                onClick={() => removeCard(idx)}
                title="Click to remove"
              >
                {card?.img_full_url ? (
                  <CardImage src={card.img_full_url} alt={card.name} wrapperClassName="deck-card-picker-hand-img-wrap" />
                ) : (
                  <div className="deck-card-picker-hand-placeholder"><UserOutlined /></div>
                )}
              </div>
            );
          })}
          {Array.from({ length: Math.max(0, maxSelection - selected.length) }).map((_, i) => (
            <div key={`empty-${i}`} className="deck-card-picker-hand-slot deck-card-picker-hand-slot-empty" />
          ))}
        </div>
      </div>
      <div className="deck-card-picker-deck">
        <Text type="secondary" strong>
          Deck (click to add to hand)
        </Text>
        <Row gutter={[8, 8]} className="deck-card-picker-deck-grid">
          {deckCards.map((dc) => {
            const inHand = countByCard[dc.card_id] || 0;
            const available = Math.max(0, dc.quantity - inHand);
            const canAdd = selected.length < maxSelection && available > 0;
            return (
              <Col xs={8} sm={6} md={4} key={dc.card_id}>
                <Card
                  size="small"
                  className={`deck-card-picker-deck-card ${canAdd ? 'deck-card-picker-deck-card-clickable' : ''}`}
                  onClick={canAdd ? () => addCard(dc.card_id) : undefined}
                >
                  <div className="deck-card-picker-deck-card-img-wrap">
                    {dc.img_full_url ? (
                      <CardImage src={dc.img_full_url} alt={dc.name} wrapperClassName="deck-card-picker-deck-img-wrap" />
                    ) : (
                      <div className="deck-card-picker-deck-placeholder"><UserOutlined /></div>
                    )}
                    {dc.quantity > 1 && (
                      <span className="deck-card-picker-deck-qty">×{dc.quantity}</span>
                    )}
                    {inHand > 0 && (
                      <span className="deck-card-picker-deck-in-hand">−{inHand}</span>
                    )}
                  </div>
                  <Text className="deck-card-picker-deck-name" ellipsis title={dc.name}>
                    {dc.name}
                  </Text>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    </div>
  );
}
